import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Clock, ShoppingCart, ArrowDownCircle, ArrowUpCircle, Gift } from "lucide-react";
import { toast } from "sonner";
import { PaymentModal } from "@/components/PaymentModal";

const fmt = (n) => n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Credits() {
  const { user, refresh } = useAuth();
  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState({ batches: [], ledger: [] });
  const [loadingPack, setLoadingPack] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);

  useEffect(() => {
    api.get("/credits/packs").then(({ data }) => setPacks(data)).catch(() => {});
    api.get("/credits/balance").then(({ data }) => setBalance(data)).catch(() => {});
    api.get("/credits/history").then(({ data }) => setHistory(data)).catch(() => {});
    refresh();
    // eslint-disable-next-line
  }, []);

  const buy = async (pack) => {
    setLoadingPack(pack.key);
    try {
      const { data } = await api.post("/credits/checkout", {
        origin_url: window.location.origin,
        pack_key: pack.key,
      });
      setSelectedPack(pack);
      setCheckoutData(data);
      setModalOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingPack(null);
    }
  };

  const best = packs.length ? Math.min(...packs.map((p) => p.unit_price_ttc)) : 0;
  const available = balance?.available ?? user?.credits ?? 0;

  return (
    <DashboardLayout title="Crédits">
      {/* Balance banner */}
      <div className="mb-8 grid grid-cols-1 lg:grid-cols-12 gap-0 border border-zinc-800">
        <div className="lg:col-span-7 p-8 border-b lg:border-b-0 lg:border-r border-zinc-800">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-500 mb-4">Solde actuel</div>
          <div className="flex items-baseline gap-3">
            <div className="font-display text-7xl font-extrabold tracking-tighter" data-testid="credits-balance">
              {balance?.is_admin ? "∞" : available}
            </div>
            <div className="text-zinc-500 text-lg">crédit{available > 1 ? "s" : ""}</div>
          </div>
          <div className="text-sm text-zinc-400 mt-3">
            {balance?.is_admin
              ? "Compte administrateur — étiquettes illimitées"
              : `1 crédit = 1 étiquette imprimée · ${available === 0 ? "Achetez un pack pour continuer" : "Prêt à imprimer"}`}
          </div>
          {balance?.expiring_soon?.length > 0 && (
            <div className="mt-6 border border-orange-500/40 bg-orange-500/5 p-3 flex items-start gap-2">
              <Clock className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
              <div className="text-xs text-zinc-300">
                {balance.expiring_soon.map((b, i) => (
                  <div key={i}>
                    <span className="text-orange-400 font-semibold">{b.amount} crédit{b.amount > 1 ? "s" : ""}</span>{" "}
                    expire{b.amount > 1 ? "nt" : ""} dans {b.days_left} jour{b.days_left > 1 ? "s" : ""}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-5 p-8 bg-zinc-950 flex flex-col justify-center">
          <div className="flex items-start gap-3">
            <Sparkles className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <div className="text-xs text-zinc-300 leading-relaxed">
              <div className="font-semibold text-white mb-1">Modèle à l'usage</div>
              Payez uniquement ce que vous consommez. Pas d'abonnement, pas d'engagement. Les crédits sont valables 1 an.
            </div>
          </div>
        </div>
      </div>

      {/* Pack grid */}
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="font-display text-2xl font-bold tracking-tight">Recharger mon compte</h2>
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Prix TTC · Valable 1 an</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-0 border border-zinc-800">
        {packs.map((p) => {
          const isBest = p.unit_price_ttc === best;
          const savingsPct = packs.length ? Math.round((1 - p.unit_price_ttc / packs[0].unit_price_ttc) * 100) : 0;
          return (
            <div
              key={p.key}
              data-testid={`pack-card-${p.key}`}
              className={`p-6 border-r last:border-r-0 md:[&:nth-child(2n)]:border-r-0 lg:[&:not(:last-child)]:border-r lg:[&:nth-child(2n)]:border-r border-zinc-800 flex flex-col ${
                isBest ? "bg-red-500/5 relative" : ""
              }`}
            >
              {isBest && (
                <div className="absolute top-0 right-0 bg-red-500 text-white text-[9px] font-mono uppercase tracking-widest px-2 py-1">
                  Meilleure offre
                </div>
              )}
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{p.label}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <div className="font-display text-4xl font-extrabold tracking-tighter">{p.credits.toLocaleString("fr-FR")}</div>
                <div className="text-zinc-500 text-xs">étiq.</div>
              </div>
              <div className="mt-4 flex items-baseline gap-1">
                <div className="font-display text-3xl font-bold tracking-tighter">{fmt(p.price_ttc)}</div>
                <div className="text-zinc-500 text-sm">€</div>
              </div>
              <div className="text-[11px] font-mono text-zinc-500 mt-1">
                {fmt(p.unit_price_ttc)}€ / étiq.
              </div>
              {savingsPct > 0 && (
                <div className="text-[10px] font-mono text-green-400 uppercase tracking-widest mt-2">
                  −{savingsPct}% vs pack 100
                </div>
              )}
              <Button
                data-testid={`buy-pack-${p.key}`}
                onClick={() => buy(p)}
                disabled={loadingPack === p.key || balance?.is_admin}
                className={`mt-6 rounded-none w-full ${isBest ? "bg-red-500 hover:bg-red-600" : "bg-zinc-900 hover:bg-zinc-800 border border-zinc-700"}`}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {loadingPack === p.key ? "Chargement…" : "Acheter"}
              </Button>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-zinc-500 mt-4 font-mono">
        💳 Paiement par PayPal ou email · TVA 20% incluse · Facture PDF automatique
      </p>
      <p className="text-[11px] text-zinc-600 mt-1 font-mono">
        Vos crédits sont activés manuellement sous 24h (jours ouvrés) après réception du paiement
      </p>

      {/* History */}
      <div className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Historique des mouvements</h2>
        <div className="border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 border-b border-zinc-800">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3 text-right">Crédits</th>
                <th className="px-4 py-3">Détails</th>
              </tr>
            </thead>
            <tbody>
              {history.ledger.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-sm text-zinc-500">
                  Aucun mouvement pour l'instant.
                </td></tr>
              )}
              {history.ledger.slice(0, 60).map((l) => (
                <tr key={l.id} data-testid={`ledger-row-${l.id}`} className="border-b border-zinc-800 hover:bg-zinc-900/60">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">
                    {new Date(l.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-4 py-3">
                    {l.type === "credit" ? (
                      l.source === "signup_gift" ? (
                        <span className="inline-flex items-center gap-1 text-green-400"><Gift className="w-3.5 h-3.5" /> Crédits offerts</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-green-400"><ArrowUpCircle className="w-3.5 h-3.5" /> Achat</span>
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1 text-zinc-400"><ArrowDownCircle className="w-3.5 h-3.5" /> Impression</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 font-mono text-sm text-right font-semibold ${l.type === "credit" ? "text-green-400" : "text-zinc-300"}`}>
                    {l.type === "credit" ? "+" : "−"}{l.amount}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-zinc-500">
                    {l.pack_key ? `Pack ${l.amount} étiquettes` : l.type === "debit" ? `Étiquette n°${(l.label_history_id || "").slice(-6) || "—"}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        packInfo={selectedPack}
        checkoutData={checkoutData}
      />
    </DashboardLayout>
  );
}
